import type { ErrorHandlersType } from "@/hooks/useBackendMutation";
import { State } from "@/state";

/** ErrorsObject shape returned on mutation/query payloads (see Errors fragment). */
export type GraphqlPayloadErrors = {
  authenticationError?: { message?: string | null } | null;
  authorizationError?: { message?: string | null } | null;
  rateLimitError?: { message?: string | null } | null;
  notFoundError?: { message?: string | null } | null;
  subscriptionError?: { message?: string | null } | null;
  genericErrors?: Array<{ message?: string | null }> | null;
  fieldErrors?: Array<{
    message?: string | null;
    field?: string | null;
  }> | null;
};

export function formatApolloError(error: unknown): string {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return "Something went wrong. Please try again.";
}

function relayPayloadErrors(
  errors: GraphqlPayloadErrors,
  handlers: ErrorHandlersType,
): void {
  if (errors.authenticationError && handlers.authenticationError) {
    handlers.authenticationError(errors.authenticationError);
  }
  if (errors.authorizationError && handlers.authorizationError) {
    handlers.authorizationError(errors.authorizationError);
  }
  if (errors.rateLimitError && handlers.rateLimitError) {
    handlers.rateLimitError(errors.rateLimitError);
  }
  if (errors.notFoundError && handlers.notFoundError) {
    handlers.notFoundError(errors.notFoundError);
  }
  if (errors.subscriptionError && handlers.subscriptionError) {
    handlers.subscriptionError(errors.subscriptionError);
  }
  if (errors.genericErrors?.length && handlers.genericErrors) {
    for (const err of errors.genericErrors) {
      handlers.genericErrors(err);
    }
  }
  if (errors.fieldErrors?.length && handlers.fieldErrors) {
    for (const err of errors.fieldErrors) {
      handlers.fieldErrors(err);
    }
  }
}

/** Show payload errors via snackbar. Returns true if any error was relayed. */
export function relayGraphqlPayloadErrors(
  errors: GraphqlPayloadErrors | null | undefined,
  isAuthenticated = true,
): boolean {
  if (!errors) return false;
  const setSnackbarMessage = State.getState().setSnackbarMessage;
  const handlers: ErrorHandlersType = {
    authenticationError: (e) =>
      setSnackbarMessage(e.message ?? "Authentication required."),
    authorizationError: (e) =>
      setSnackbarMessage(e.message ?? "Not authorized."),
    rateLimitError: (e) =>
      setSnackbarMessage(e.message ?? "Rate limit exceeded."),
    notFoundError: (e) => setSnackbarMessage(e.message ?? "Not found."),
    subscriptionError: (e) => {
      if (!isAuthenticated)
        setSnackbarMessage(e.message ?? "Subscription required.");
    },
    genericErrors: (e) =>
      setSnackbarMessage(e.message ?? "Something went wrong."),
    fieldErrors: (e) =>
      setSnackbarMessage(
        e.field
          ? `${e.field}: ${e.message ?? "Invalid value."}`
          : (e.message ?? "Invalid value."),
      ),
  };
  relayPayloadErrors(errors, handlers);
  return true;
}
